import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { CheckCircle2, AlertTriangle, UserPlus } from 'lucide-react'
import { format } from 'date-fns'
import HelpLink from '../components/HelpLink'

// brand_admins invite teammates to the brand they're working in. The server (api/invite-user.js) checks the
// inviter's real role and brand access: brand_admins can only invite brand_admins, to brands they already have.
// (The viewer role isn't enforced in the UI yet, so it isn't offered.)
// Editing or removing users stays with super admins on the Users page.
const EMPTY = { email: '', full_name: '', role: 'brand_admin' }

export default function InviteTeammate() {
  const { activeBrand } = useAuth()
  const [form, setForm] = useState(EMPTY)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const [team, setTeam] = useState(null)
  const [teamError, setTeamError] = useState(null)

  // Teammates with access to this brand — read through the server, which applies the same permission rule as invites
  async function loadTeam() {
    if (!activeBrand) return
    setTeamError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Your session expired — please sign in again.')
      const res = await fetch(`/api/invite-user?brand_id=${encodeURIComponent(activeBrand.id)}`, { headers: { Authorization: `Bearer ${session.access_token}` } })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Could not load the team')
      setTeam(data.users || [])
    } catch (e) { setTeamError(e.message); setTeam([]) }
  }
  useEffect(() => { loadTeam() }, [activeBrand?.id])

  async function send() {
    setResult(null)
    if (!form.email.trim()) { setResult({ error: 'Email is required.' }); return }
    setSending(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Your session expired — please sign in again.')
      const res = await fetch('/api/invite-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ email: form.email.trim(), full_name: form.full_name.trim(), role: form.role, brand_ids: [activeBrand.id] }),
      })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Invite failed')
      setResult({ ok: `Invite sent to ${form.email.trim()}. They'll get an email to set their password.` })
      setForm(EMPTY)
      loadTeam()
    } catch (e) {
      setResult({ error: e.message })
    }
    setSending(false)
  }

  if (!activeBrand) return null

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Team <HelpLink section="team" /></h1>
          <div style={{ fontSize: '13px', color: 'var(--text-3)', marginTop: '4px' }}>Who has access to {activeBrand.name}, and invite someone new.</div>
        </div>
      </div>

      {result?.ok && <div className="alert alert-success" style={{ marginBottom: '16px' }}><CheckCircle2 size={14} /> {result.ok}</div>}
      {result?.error && <div className="alert alert-danger" style={{ marginBottom: '16px' }}><AlertTriangle size={14} /> {result.error}</div>}

      <div className="card" style={{ padding: '20px', maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="name@company.com" />
        </div>
        <div className="form-group">
          <label className="form-label">Name</label>
          <input className="input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Optional" />
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>They'll get the same access as you.</div>
        <div>
          <button className="btn btn-primary" onClick={send} disabled={sending || !form.email.trim()}><UserPlus size={14} />{sending ? 'Sending…' : 'Send invite'}</button>
        </div>
      </div>
      <div className="card" style={{ marginTop: '20px', maxWidth: '760px' }}>
        <div style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>People with access{team ? ` (${team.length})` : ''}</div>
        {team === null ? <div style={{ padding: '24px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : teamError ? <div className="alert alert-danger" style={{ margin: '12px 16px' }}><AlertTriangle size={14} /> {teamError}</div>
          : !team.length ? <div className="empty-state"><p>No one yet</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Status</th></tr></thead>
                <tbody>
                  {team.map(u => (
                    <tr key={u.id}>
                      <td>{u.full_name || '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{u.email}</td>
                      <td>{u.joined
                        ? <span className="badge badge-success" title={u.last_sign_in_at ? `Last signed in ${format(new Date(u.last_sign_in_at), 'MMM d, yyyy')}` : undefined}>Active</span>
                        : <span className="badge badge-neutral" title={u.invited_at ? `Invited ${format(new Date(u.invited_at), 'MMM d, yyyy')}` : undefined}>Invite pending</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '12px' }}>To change someone's access or remove them, ask your account admin.</div>
    </div>
  )
}
